import { useCallback, useEffect, useMemo, useState } from "react";
import { TimeClockAction } from "@iq/models";
import { clockIn, clockOut, getMyClockStatus } from "../services/timeClockApi";

function HeaderClockAction() {
  const [clockState, setClockState] = useState({
    isLoading: true,
    isBusy: false,
    isClockedIn: false,
    clockInAt: null,
    errorMessage: "",
  });

  useEffect(() => {
    let cancelled = false;

    async function hydrateClockStatus() {
      setClockState((prev) => ({ ...prev, isLoading: true }));

      try {
        const status = await getMyClockStatus();
        if (cancelled) {
          return;
        }

        setClockState((prev) => ({
          ...prev,
          isLoading: false,
          isClockedIn: Boolean(status?.isClockedIn),
          clockInAt: status?.clockInAt || null,
          errorMessage: "",
        }));
      } catch (error) {
        if (cancelled) {
          return;
        }

        setClockState((prev) => ({
          ...prev,
          isLoading: false,
          errorMessage: error?.message || "Failed to load clock status",
        }));
      }
    }

    hydrateClockStatus();

    return () => {
      cancelled = true;
    };
  }, []);

  const clockInLabel = useMemo(() => {
    if (!clockState.clockInAt) {
      return "";
    }

    const parsedClockInAt = new Date(clockState.clockInAt);
    if (Number.isNaN(parsedClockInAt.getTime())) {
      return "";
    }

    return parsedClockInAt.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  }, [clockState.clockInAt]);

  const handleClockToggle = useCallback(async () => {
    if (clockState.isLoading || clockState.isBusy) {
      return;
    }

    setClockState((prev) => ({ ...prev, isBusy: true }));

    try {
      const handler = clockState.isClockedIn ? clockOut : clockIn;
      const result = await handler();

      setClockState((prev) => ({
        ...prev,
        isBusy: false,
        isClockedIn: Boolean(result?.isClockedIn),
        clockInAt: result?.clockInAt || null,
        errorMessage: "",
      }));
    } catch (error) {
      setClockState((prev) => ({
        ...prev,
        isBusy: false,
        errorMessage: error?.message || "Failed to update time clock status",
      }));
    }
  }, [clockState.isBusy, clockState.isClockedIn, clockState.isLoading]);

  return (
    <TimeClockAction
      className="header-clock-button"
      activeClassName="is-active"
      hideLabelOnMobile
      iconSize={16}
      isLoading={clockState.isLoading}
      isBusy={clockState.isBusy}
      isClockedIn={clockState.isClockedIn}
      clockInLabel={clockInLabel}
      errorMessage={clockState.errorMessage}
      onToggle={handleClockToggle}
    />
  );
}

export default HeaderClockAction;
